import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  AmbitoPropagazione, ConteggioVariante, DiffPropagazioneArticolo, ModificaCampoArticolo,
  RuoloConteggio, conteggioRigheDaAggiornare,
} from '../../../_helpers/articolo-propagazione';
import {
  AnteprimaImpattoOrdiniArticolo, PropagaAiOrdiniArticoloRequest,
} from '../../../_services/articoli.service';

// Feature 121 (issue #3372) — dialogo di propagazione, mostrato DOPO il salvataggio dell'articolo o
// della variante. Annullarlo annulla la sola propagazione, non il salvataggio (spec FR-003).
//
// standalone: false, dichiarato in SharedModule accanto ad ArticoliProdottiCreateComponent che lo
// apre. Componente NUOVO e non generalizzazione di PropagaOrdiniModalComponent: quello è
// specializzato su campi utente, sedi eliminate e gruppo indirizzo — tre cose che qui non esistono —
// e non ha la sezione unità di misura, che qui è obbligatoria (research D20).
@Component({
  selector: 'app-propaga-ordini-articolo-modal',
  templateUrl: './propaga-ordini-articolo-modal.component.html',
  styleUrls: ['./propaga-ordini-articolo-modal.component.scss'],
  standalone: false,
})
export class PropagaOrdiniArticoloModalComponent implements OnInit {
  @Input() diff: DiffPropagazioneArticolo;
  @Input() anteprima: AnteprimaImpattoOrdiniArticolo;
  @Input() orgId: number;
  @Input() articoloId: number;

  // Valorizzato SOLO nel dialogo di variante (US3): il blocco viaggia come `varianti: [...]` e i
  // numeri mostrati sono quelli di QUELLA variante, mai quelli dell'articolo (spec FR-016, FR-010).
  @Input() varianteId: number | null = null;

  // Ambito: l'operatore sceglie, non è una regola fissa di sistema (spec FR-009).
  soloInCorso = true;

  constructor(private modalController: ModalController) {}

  ngOnInit() {
    if (!this.diff) this.diff = { articolo: [] };
    if (!this.anteprima) this.anteprima = { disponibile: false, varianti: [] };
  }

  get dialogoDiVariante(): boolean { return this.varianteId !== null; }

  get conteggioDisponibile(): boolean {
    return this.anteprima.disponibile && !!this.anteprima.articolo;
  }

  private get ambito(): AmbitoPropagazione { return this.soloInCorso ? 'inCorso' : 'totali'; }

  // Conteggi della variante di cui questo dialogo parla; `null` nel dialogo di articolo.
  private get conteggiVariante(): ConteggioVariante | null {
    if (this.varianteId === null) return null;
    const variante = this.anteprima.varianti.find(v => v.varianteId === this.varianteId);
    return variante ? { righeInCorso: variante.righeInCorso, righeTotali: variante.righeTotali } : null;
  }

  // ⚠️ UNICO punto in cui si decide quale numero mostrare, delegato alla funzione pura dell'helper:
  // pulsante ed etichette d'ambito non possono divergere (spec FR-009, FR-010, SC-002).
  private conteggio(ambito: AmbitoPropagazione, ruolo: RuoloConteggio): number | null {
    return conteggioRigheDaAggiornare(
      this.chiaviSelezionate(), ambito, this.anteprima, ruolo, this.conteggiVariante);
  }

  etichettaAmbito(ambito: AmbitoPropagazione): number | null {
    return this.conteggio(ambito, 'etichettaAmbito');
  }

  get righeDaAggiornare(): number | null { return this.conteggio(this.ambito, 'pulsante'); }

  // Senza conteggio l'etichetta perde il numero, non l'abilitazione (spec FR-012).
  get etichettaConferma(): string {
    const righe = this.righeDaAggiornare;
    return righe === null ? 'Aggiorna le righe collegate' : `Aggiorna ${righe} righe`;
  }

  // Non esiste una propagazione vuota; e una selezione che aggiornerebbe zero righe non è
  // confermabile (spec FR-017). ⚠️ MAI disabilitata per ASSENZA del conteggio: legare le due
  // condizioni vanificherebbe il fail-open di FR-012 — l'operatore vedrebbe il dialogo e non
  // potrebbe confermare.
  get confermaDisabilitata(): boolean {
    if (this.nessunCampoSpuntato) return true;
    return this.conteggioDisponibile && this.righeDaAggiornare === 0;
  }

  get nessunCampoSpuntato(): boolean { return this.chiaviSelezionate().length === 0; }

  // ── Sezioni del dialogo (spec FR-005) ───────────────────────────────────

  get modificheArticolo(): ModificaCampoArticolo[] {
    return this.diff.articolo.filter(m => m.sezione === 'articolo');
  }

  get modificheVariante(): ModificaCampoArticolo[] {
    return this.diff.articolo.filter(m => m.sezione === 'variante');
  }

  // Il gruppo `udm` si mostra con UNA SOLA casella: la seconda voce (MagUdM) è la stessa sorgente e
  // comparirebbe come una scelta separata che non esiste (spec FR-006).
  get modificheUdM(): ModificaCampoArticolo[] {
    return this.diff.articolo.filter(m => m.sezione === 'udm' && m.chiave !== 'MagUdM');
  }

  get gruppoUdM(): ModificaCampoArticolo | undefined {
    return this.diff.articolo.find(m => m.gruppo === 'udm' && m.chiave === 'UdM');
  }

  // Una sola casella governa UdM e MagUdM: la spunta si replica sulla voce nascosta, così la
  // richiesta porta entrambe le chiavi o nessuna.
  onGruppoUdMCambiato(selezionato: boolean): void {
    this.diff.articolo
      .filter(m => m.gruppo === 'udm')
      .forEach(m => m.selezionato = selezionato);
  }

  // ── Avvisi con i numeri dell'ambito selezionato (spec FR-013, FR-015) ───

  get righeConQuantita(): number | null {
    if (!this.conteggioDisponibile) return null;
    return this.soloInCorso
      ? this.anteprima.articolo!.righeConQuantitaInCorso
      : this.anteprima.articolo!.righeConQuantitaTotali;
  }

  get righeSenzaVariante(): number | null {
    if (!this.conteggioDisponibile) return null;
    return this.soloInCorso
      ? this.anteprima.articolo!.righeSenzaVarianteInCorso
      : this.anteprima.articolo!.righeSenzaVarianteTotali;
  }

  // Caso zero di spec FR-017: l'avviso cambia testo e la conferma è impedita se quella è l'unica
  // casella spuntata. Il dialogo RESTA APERTO — gli altri campi restano propagabili.
  get nessunaRigaSenzaVariante(): boolean {
    return this.conteggioDisponibile && this.righeSenzaVariante === 0;
  }

  // ── Composizione della richiesta ───────────────────────────────────────

  private chiaviSelezionate(): string[] {
    return this.diff.articolo.filter(m => m.selezionato).map(m => m.chiave);
  }

  conferma(): void {
    const chiavi = this.chiaviSelezionate();
    const request: PropagaAiOrdiniArticoloRequest = {
      orgId: this.orgId,
      articoloId: this.articoloId,
      soloInCorso: this.soloInCorso,
      // Nel dialogo di variante la selezione viaggia nel blocco della variante, mai fra i campi
      // articolo: il predicato di applicazione è diverso (spec FR-023).
      campiArticolo: this.dialogoDiVariante ? [] : chiavi,
      varianti: this.dialogoDiVariante ? [{ varianteId: this.varianteId!, campi: chiavi }] : [],
    };
    this.modalController.dismiss(request, 'confirm');
  }

  // "Non aggiornare": nessuna riga cambia e la modifica all'articolo resta salvata.
  annulla(): void {
    this.modalController.dismiss(null, 'cancel');
  }
}
